import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, X, Send, Bot, User, GripVertical, Sparkles } from 'lucide-react';

// ============ KNOWLEDGE BASE ============
const getResponse = (input) => {
  const q = input.toLowerCase();
  
  if (/^(hi|hello|hey|namaste)/.test(q)) {
    return { text: "Hello! 👋 Welcome to P.R. Net Com! How can I help you?", replies: ["Our Services", "Contact Info", "About Us"] };
  }
  if (/service|offer|provide/.test(q)) {
    return { text: "**Our Services:**\n• Computer Sales & Service\n• Server Maintenance\n• Network Installation\n• Software Supply\n• Network Cabling\n• Cisco Solutions\n• WiFi Solutions\n• AMC Services\n• IT Facility Management\n• Online UPS Solutions\n• Battery Supply (SMF/Tubular)", replies: ["Contact Us", "Get Quote"] };
  }
  if (/contact|phone|email|call/.test(q)) {
    return { text: "**Contact Us:**\n📞 +91 9674876102, +91 9830220126\n📧 prnetcom.in@gmail.com\n📍 155, Dumdum Road, Ghoshpara, Kolkata - 700074", replies: ["Our Services", "About Us"] };
  }
  if (/about|company|who/.test(q)) {
    return { text: "**About P.R. Net Com:**\nISO 9001:2008 Certified IT solutions company with 11+ years of experience. We serve major clients including UCO Bank, SBI, and government organizations.", replies: ["Our Services", "Contact Us"] };
  }
  if (/ups|power|uninterrupt/.test(q)) {
    return { text: "**Online UPS Solutions:**\n• Online UPS Systems for critical IT infrastructure\n• UPS Installation & Maintenance\n• Battery Replacement\n\n**Partners:** Switching AVO Electro Power Limited, AMARON QUANTA", replies: ["Battery Info", "Contact Us"] };
  }
  if (/battery|smf|tubular|amaron/.test(q)) {
    return { text: "**Battery Supply:**\n• SMF Batteries - Sealed Maintenance Free\n• Tubular Batteries - Long-lasting backup\n• AMARON QUANTA - Premium brand", replies: ["UPS Solutions", "Contact Us"] };
  }
  if (/network|lan|cabling/.test(q)) {
    return { text: "**Network Services:**\n• LAN Setup & Configuration\n• Network Cabling\n• WiFi Solutions\n• Cisco L2 Switch Installation\n\nWe've completed projects for UCO Bank Data Center and multiple bank branches.", replies: ["Get Quote", "Our Clients"] };
  }
  if (/client|customer|bank/.test(q)) {
    return { text: "**Our Clients:**\n• UCO Bank\n• State Bank of India\n• West Bengal State Co-operative Bank\n• Paschim Banga Gramin Bank\n• South Dumdum Municipality", replies: ["Our Services", "Contact Us"] };
  }
  if (/price|cost|quote|rate/.test(q)) {
    return { text: "**Get a Quote:**\nPricing depends on your requirements.\n\n📞 Call: +91 9674876102\n📧 Email: prnetcom.in@gmail.com", replies: ["Our Services", "Contact Us"] };
  }
  if (/amc|maintenance|contract/.test(q)) {
    return { text: "**AMC Services:**\n• Regular preventive maintenance\n• Priority technical support\n• Hardware repair & replacement\n• Network monitoring\n• On-site support", replies: ["Get Quote", "Contact Us"] };
  }
  if (/thank/.test(q)) {
    return { text: "You're welcome! 😊 Anything else I can help with?", replies: ["Services", "Contact"] };
  }
  if (/bye|goodbye/.test(q)) {
    return { text: "Thank you for chatting! Have a great day! 🙏", replies: ["Get Quote", "Call Us"] };
  }
  
  return { text: "I can help you with:\n\n🔧 **Our Services**\n🏢 **About Us**\n👥 **Our Clients**\n📞 **Contact Info**\n\nWhat would you like to know?", replies: ["Our Services", "Contact Info", "Get Quote"] };
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: "👋 Hi! I'm your P.R. Net Com assistant. How can I help?", replies: ["Our Services", "Contact Info", "About Us"] }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Floating icon position
  const [iconPos, setIconPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  
  // Use ref for input value to avoid re-renders
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Initialize position
  useEffect(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    setIconPos({
      x: window.innerWidth - 80,
      y: window.innerHeight - 150
    });
  }, []);

  // Scroll messages to bottom
  useEffect(() => {
    if (messagesEndRef.current && messagesEndRef.current.parentElement) {
      const container = messagesEndRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isTyping]);

  // Drag handlers for floating icon
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - iconPos.x,
      y: e.clientY - iconPos.y
    };
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.touches[0].clientX - iconPos.x,
      y: e.touches[0].clientY - iconPos.y
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setIconPos({
        x: Math.max(0, Math.min(window.innerWidth - 60, clientX - dragStart.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 60, clientY - dragStart.current.y))
      });
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDragging]);

  // Send message - read from input ref directly
  const sendMessage = () => {
    if (!inputRef.current) return;
    const text = inputRef.current.value.trim();
    if (!text || isTyping) return;

    setMessages(prev => [...prev, { type: 'user', text }]);
    inputRef.current.value = ''; // Clear input
    setIsTyping(true);

    setTimeout(() => {
      const response = getResponse(text);
      setMessages(prev => [...prev, { type: 'bot', ...response }]);
      setIsTyping(false);
    }, 500);
  };

  // Handle quick reply
  const handleQuickReply = (text) => {
    if (isTyping) return;
    setMessages(prev => [...prev, { type: 'user', text }]);
    setIsTyping(true);

    setTimeout(() => {
      const response = getResponse(text);
      setMessages(prev => [...prev, { type: 'bot', ...response }]);
      setIsTyping(false);
    }, 500);
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format text with bold
  const formatText = (text) => {
    return text.split('\n').map((line, i) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <div key={i} dangerouslySetInnerHTML={{ __html: formatted }} />;
    });
  };

  // Chat window component - defined inside to access state
  const ChatWindowContent = () => (
    <>
      {/* Header */}
      <div className="simple-chat-header">
        <div className="simple-chat-header-left">
          <Sparkles size={18} />
          <span>P.R. Net Com</span>
        </div>
        <button className="simple-chat-close" onClick={() => setIsOpen(false)}>
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="simple-chat-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`simple-chat-bubble ${msg.type}`}>
            <div className="simple-bubble-icon">
              {msg.type === 'bot' ? <Bot size={16} /> : <User size={16} />}
            </div>
            <div className="simple-bubble-content">
              <div className="simple-bubble-text">{formatText(msg.text)}</div>
              {msg.type === 'bot' && msg.replies && idx === messages.length - 1 && (
                <div className="simple-quick-replies">
                  {msg.replies.map((reply, i) => (
                    <button 
                      key={i} 
                      onClick={() => handleQuickReply(reply)}
                      disabled={isTyping}
                      type="button"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="simple-chat-bubble bot">
            <div className="simple-bubble-icon"><Bot size={16} /></div>
            <div className="simple-bubble-content">
              <div className="simple-typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Uncontrolled to prevent re-renders */}
      <div className="simple-chat-input-area">
        <input
          ref={inputRef}
          type="text"
          defaultValue=""
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={isTyping}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        <button onClick={sendMessage} disabled={isTyping} type="button">
          <Send size={18} />
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Floating Draggable Icon */}
      <div
        className="simple-chat-float"
        style={{ left: iconPos.x, top: iconPos.y }}
      >
        {/* Drag handle */}
        <div
          className="simple-chat-drag-handle"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <GripVertical size={14} />
        </div>
        
        {/* Toggle button */}
        <button
          className="simple-chat-toggle"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        </button>
      </div>

      {/* Chat Window - Desktop Portal */}
      {isOpen && !isMobile && ReactDOM.createPortal(
        <div className="simple-chat-container">
          <div className="simple-chat-window">
            <ChatWindowContent />
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Chat - Portal */}
      {isOpen && isMobile && ReactDOM.createPortal(
        <div className="simple-chat-mobile-overlay">
          <div className="simple-chat-window">
            <ChatWindowContent />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChatBot;
